import { NativeAppEvents } from '@/components/NativeAppEvents';
import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { Toaster } from '@/components/ui/sonner';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { authAPI } from '@/lib/api';
import {
  NATIVE_AUTH_VERIFIER_KEY,
  parseNativeAuthUrl,
  resolveNativeAuthTarget,
} from '@/lib/nativeAuth';
import { toast } from 'sonner';

// Pages
import LandingPage from '@/pages/LandingPage';
import BusinessLandingPage from '@/pages/BusinessLandingPage';
import PublicFacilityPage from '@/pages/PublicFacilityPage';
import AuthCallback from '@/pages/AuthCallback';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import Dashboard from '@/pages/Dashboard';
import LeaguesPage from '@/pages/LeaguesPage';
import LeagueDetailPage from '@/pages/LeagueDetailPage';
import CreateLeaguePage from '@/pages/CreateLeaguePage';
import MatchesPage from '@/pages/MatchesPage';
import MatchDetailPage from '@/pages/MatchDetailPage';
import MatchLivePage from '@/pages/MatchLivePage';
import WatchPairPage from '@/pages/WatchPairPage';
import CreateMatchPage from '@/pages/CreateMatchPage';
import CreateCompetitionPage from '@/pages/CreateCompetitionPage';
import CompetitionDetailPage from '@/pages/CompetitionDetailPage';
import SearchPage from '@/pages/SearchPage';
import ProfilePage from '@/pages/ProfilePage';
import CreateAnnouncementPage from '@/pages/CreateAnnouncementPage';
import AnnouncementsPage from '@/pages/AnnouncementsPage';
import RefereesListPage from '@/pages/RefereesListPage';
import RefereeProfilePage from '@/pages/RefereeProfilePage';
import AdminDashboard from '@/pages/AdminDashboard';
import FacilitiesPage from '@/pages/FacilitiesPage';
import CreateFacilityPage from '@/pages/CreateFacilityPage';
import FacilityDetailPage from '@/pages/FacilityDetailPage';
import FacilityDashboardPage from '@/pages/FacilityDashboardPage';
import UpgradeFacilityPage from '@/pages/UpgradeFacilityPage';
import BookingConfirmPage from '@/pages/BookingConfirmPage';
import QuotaConfirmPage from '@/pages/QuotaConfirmPage';
import HelpPage from '@/pages/HelpPage';
import InstallPwaPage from '@/pages/InstallPwaPage';
import { AccountDeletionPage, PrivacyPolicyPage } from '@/pages/LegalPages';

import '@/App.css';

// AppRouter handles OAuth callback detection synchronously
const AppRouter = () => {
  const location = useLocation();

  // Check URL hash for OAuth tokens SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  // - session_id: legacy Emergent OAuth callback
  // - auth_token: native Google OAuth callback (Phase 3+)
  if (location.hash?.includes('session_id=') || location.hash?.includes('auth_token=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/business" element={<BusinessLandingPage />} />
      <Route path="/p/:facilityId" element={<PublicFacilityPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/install-pwa" element={<InstallPwaPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/help" element={<HelpPage />} />
      {/* Stripe comes back here also inside the app's in-app browser, which has no login */}
      <Route path="/quota/confirm" element={<QuotaConfirmPage />} />
      
      {/* Protected routes with layout */}
      <Route element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/leagues" element={<LeaguesPage />} />
        <Route path="/leagues/new" element={<CreateLeaguePage />} />
        <Route path="/leagues/:id" element={<LeagueDetailPage />} />
        <Route path="/leagues/:id/edit" element={<CreateLeaguePage />} />
        <Route path="/matches" element={<MatchesPage />} />
        <Route path="/matches/new" element={<CreateMatchPage />} />
        <Route path="/matches/:id" element={<MatchDetailPage />} />
        <Route path="/matches/:id/live" element={<ProtectedRoute><MatchLivePage /></ProtectedRoute>} />
        <Route path="/watch/pair" element={<ProtectedRoute><WatchPairPage /></ProtectedRoute>} />
        <Route path="/matches/:id/edit" element={<CreateMatchPage />} />
        <Route path="/competitions/new" element={<CreateCompetitionPage />} />
        <Route path="/competitions/:id/edit" element={<CreateCompetitionPage />} />
        <Route path="/competitions/:id" element={<CompetitionDetailPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/announcements/new" element={<CreateAnnouncementPage />} />
        <Route path="/referees" element={<RefereesListPage />} />
        <Route path="/referees/:userId" element={<RefereeProfilePage />} />
        <Route path="/facilities" element={<FacilitiesPage />} />
        <Route path="/facilities/new" element={<CreateFacilityPage />} />
        <Route path="/facilities/:id" element={<FacilityDetailPage />} />
        <Route path="/facilities/:id/dashboard" element={<FacilityDashboardPage />} />
        <Route path="/facilities/:id/upgrade" element={<UpgradeFacilityPage />} />
        <Route path="/facilities/:id/edit" element={<CreateFacilityPage />} />
        <Route path="/bookings/confirm" element={<BookingConfirmPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Route>

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
};

const NativeOAuthBridge = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const handled = React.useRef(new Set());

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let disposed = false;
    let listener;

    const handleUrl = async (rawUrl) => {
      if (disposed) return;
      const callback = parseNativeAuthUrl(rawUrl);
      if (!callback) return;

      const callbackId = callback.code || `error:${callback.error}`;
      if (handled.current.has(callbackId)) return;
      handled.current.add(callbackId);
      await Browser.close().catch(() => {});

      if (callback.error) {
        localStorage.removeItem(NATIVE_AUTH_VERIFIER_KEY);
        toast.error(callback.error === 'access_denied' ? 'Accesso annullato.' : 'Accesso non riuscito. Riprova.');
        navigate('/', { replace: true });
        handled.current.delete(callbackId);
        return;
      }

      const verifier = localStorage.getItem(NATIVE_AUTH_VERIFIER_KEY);
      if (!verifier) {
        toast.error('Sessione di accesso scaduta. Riprova.');
        navigate('/', { replace: true });
        return;
      }

      try {
        const { data } = await authAPI.completeMobileAuth({ code: callback.code, verifier });
        localStorage.setItem('leaguehub-token', data.token);
        localStorage.removeItem(NATIVE_AUTH_VERIFIER_KEY);
        const target = resolveNativeAuthTarget(sessionStorage.getItem('pendingRedirect') || data.target);
        sessionStorage.removeItem('pendingRedirect');
        if (await checkAuth()) {
          navigate(target, { replace: true });
        }
      } catch (error) {
        const isTerminal = error.response && error.response.status < 500;
        if (isTerminal) localStorage.removeItem(NATIVE_AUTH_VERIFIER_KEY);
        else handled.current.delete(callbackId);
        console.error('Native Google OAuth callback error:', error);
        toast.error(error.response?.data?.detail || 'Accesso non riuscito. Riprova.');
        navigate('/', { replace: true });
      }
    };

    const register = async () => {
      listener = await CapacitorApp.addListener('appUrlOpen', ({ url }) => handleUrl(url));
      if (disposed) {
        await listener.remove();
        return;
      }
      const launch = await CapacitorApp.getLaunchUrl();
      if (launch?.url) await handleUrl(launch.url);
    };

    register().catch((error) => console.error('Native OAuth listener error:', error));
    return () => {
      disposed = true;
      listener?.remove();
    };
  }, [checkAuth, navigate]);

  return null;
};

function AppShell() {
  useVersionCheck();
  return (
    <>
      <BrowserRouter>
        <NativeOAuthBridge />
        <NativeAppEvents />
        <AppRouter />
      </BrowserRouter>
      {/* Global toaster — mounted at root so it works on public pages too (e.g. login) */}
      <Toaster position="top-center" />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationsProvider>
          <AppShell />
        </NotificationsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
